import React, { useState, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { connect } from "react-redux";
import { Col, Container as Grid, Row, Card, ProgressBar, Table, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useFetch } from "hooks/api";
import moment from "moment";
import { formatCurrency, formatPercent, formatNumber } from "utils/formatNumber";
import { ProductImageURL } from "components/custom/category/listings/ProductImage";
import ExternalProductLink from "components/custom/merchandise/ExternalProductLink";
import { cut } from "utils/formatText";
import { trellisPalette } from "components/custom/analytics/palettes";
import { ButtonGroup } from "components/core/basic/ButtonGroup";
import { FaTable } from "react-icons/fa6";
import { IoStatsChart } from "react-icons/io5";
import { keepPreviousData } from "@tanstack/react-query";
import BudgetIncreaseModal from "./BudgetIncreaseModal";

const AD_TYPES = {
  SBV: "Sponsored Brands Video",
  SB: "Sponsored Brands",
  SP: "Sponsored Products",
  SD: "Sponsored Display",
};
const AD_TYPES_COLORS = {
  SBV: trellisPalette[5],
  SB: trellisPalette[4],
  SP: trellisPalette[3],
  SD: trellisPalette[2],
};

const LastFiveProductSold = ({ data, isLoading, transition }) => {
  return (
    <>
      {isLoading && (
        <div className="text-center">
          <i className="fa fa-spinner fa-spin fa-2x"></i>
        </div>
      )}

      {!isLoading && (
        <div className={transition}>
          {data?.products.map((product, i) => (
            <div key={i}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "3rem",
                }}
                className="py-3"
              >
                <div style={{ float: "left" }}>
                  <ProductImageURL url={product.product_image_url} width="50px" height="50px" />
                </div>{" "}
                <div
                  style={{
                    float: "left",
                    width: "100%",
                    height: "49px",
                    marginLeft: "10px",
                  }}
                >
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                      marginRight: "25px",
                    }}
                  >
                    <Link
                      style={{
                        textDecoration: "none",
                        fontWeight: 500,
                        fontSize: "1.5rem",
                        cursor: "pointer",
                      }}
                      to={`/user/merchandising/product/${product.id}`}
                      target="_blank"
                    >
                      {cut(product.product_title, 100)}
                    </Link>
                  </div>
                  <Row>
                    <Col>
                      <small>
                        {product.sku} - {product.asin} <ExternalProductLink asin={product.asin.toUpperCase()} />
                      </small>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const TopFiveProductsSoldToday = ({ data, isLoading, transition }) => {
  return (
    <>
      {isLoading && (
        <div className="text-center">
          <i className="fa fa-spinner fa-spin fa-2x"></i>
        </div>
      )}

      {!isLoading && (
        <div className={transition}>
          {data?.top_products.map((product, i) => (
            <Row key={i}>
              <Col xs={9} xxl={10}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    paddingRight: "3rem",
                  }}
                  className="py-3"
                >
                  <div style={{ float: "left" }}>
                    <ProductImageURL url={product.product_image_url} width="50px" height="50px" />
                  </div>{" "}
                  <div
                    style={{
                      float: "left",
                      width: "100%",
                      height: "49px",
                      marginLeft: "10px",
                    }}
                  >
                    <div
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Link
                        style={{
                          textDecoration: "none",
                          fontWeight: 500,
                          fontSize: "1.5rem",
                          cursor: "pointer",
                        }}
                        to={`/user/merchandising/product/${product.id}`}
                        target="_blank"
                      >
                        {product.product_title}
                      </Link>
                    </div>
                    <Row>
                      <Col>
                        <small>
                          {product.sku} - {product.asin} <ExternalProductLink asin={product.asin.toUpperCase()} />
                        </small>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>
              <Col xs={3} xxl={2} className="fs-3 text-center mt-3">
                {data?.top_units_sold[product.id]}
              </Col>
            </Row>
          ))}
        </div>
      )}
    </>
  );
};

const PerformanceTable = ({
  hourlyDataToCurrentHour,
  currencyFormatter,
  percentFormatter,
  liveData,
  liveadSpendData,
}) => {
  return (
    <Table striped bordered hover>
      <thead>
        <tr>
          <th>Metrics</th>
          <th>Today</th>
          <th>Yesterday</th>
          <th>%</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Units Sold</td>
          <td className="text-center">{liveData.quantity_ordered}</td>
          <td className="text-center">{hourlyDataToCurrentHour.yesterday_units_ordered}</td>
          <td className="text-center">
            {percentFormatter(
              hourlyDataToCurrentHour.yesterday_units_ordered > 0 ?
                (liveData.quantity_ordered - hourlyDataToCurrentHour.yesterday_units_ordered) /
                  hourlyDataToCurrentHour.yesterday_units_ordered
              : 0,
            )}
          </td>
        </tr>
        <tr>
          <td>Total Sales</td>
          <td className="text-center">{currencyFormatter(liveData.order_item_value)}</td>
          <td className="text-center">{currencyFormatter(hourlyDataToCurrentHour.yesterday_sales)}</td>
          <td className="text-center">
            {percentFormatter(
              hourlyDataToCurrentHour.yesterday_sales > 0 ?
                (liveData.order_item_value - hourlyDataToCurrentHour.yesterday_sales) /
                  hourlyDataToCurrentHour.yesterday_sales
              : 0,
            )}
          </td>
        </tr>
        <tr>
          <td>Ad Spend</td>
          <td className="text-center">{currencyFormatter(liveadSpendData.cost)}</td>
          <td className="text-center">{currencyFormatter(hourlyDataToCurrentHour.yesterday_cost)}</td>
          <td className="text-center">
            {percentFormatter(
              hourlyDataToCurrentHour.yesterday_cost > 0 ?
                (liveadSpendData.cost - hourlyDataToCurrentHour.yesterday_cost) / hourlyDataToCurrentHour.yesterday_cost
              : 0,
            )}
          </td>
        </tr>
        <tr>
          <td>Ad Sales</td>
          <td className="text-center">{currencyFormatter(liveadSpendData.attributed_sales_7d)}</td>
          <td className="text-center">{currencyFormatter(hourlyDataToCurrentHour.yesterday_ad_sales)}</td>
          <td className="text-center">
            {percentFormatter(
              hourlyDataToCurrentHour.yesterday_ad_sales > 0 ?
                (liveadSpendData.attributed_sales_7d - hourlyDataToCurrentHour.yesterday_ad_sales) /
                  hourlyDataToCurrentHour.yesterday_ad_sales
              : 0,
            )}
          </td>
        </tr>
        <tr>
          <td>Impressions</td>
          <td className="text-center">{formatNumber(liveadSpendData.impressions)}</td>
          <td className="text-center">{formatNumber(hourlyDataToCurrentHour.yesterday_impressions)}</td>
          <td className="text-center">
            {percentFormatter(
              hourlyDataToCurrentHour.yesterday_impressions > 0 ?
                (liveadSpendData.impressions - hourlyDataToCurrentHour.yesterday_impressions) /
                  hourlyDataToCurrentHour.yesterday_impressions
              : 0,
            )}
          </td>
        </tr>
        <tr>
          <td>Clicks</td>
          <td className="text-center">{formatNumber(liveadSpendData.clicks)}</td>
          <td className="text-center">{formatNumber(hourlyDataToCurrentHour.yesterday_clicks)}</td>
          <td className="text-center">
            {percentFormatter(
              hourlyDataToCurrentHour.yesterday_clicks > 0 ?
                (liveadSpendData.clicks - hourlyDataToCurrentHour.yesterday_clicks) /
                  hourlyDataToCurrentHour.yesterday_clicks
              : 0,
            )}
          </td>
        </tr>
        <tr>
          <td>ACOS</td>
          <td className="text-center">
            {percentFormatter(
              liveadSpendData.attributed_sales_7d > 0 ?
                hourlyDataToCurrentHour.today_cost / liveadSpendData.attributed_sales_7d
              : 0,
            )}
          </td>
          <td className="text-center">
            {percentFormatter(
              hourlyDataToCurrentHour.yesterday_ad_sales > 0 ?
                hourlyDataToCurrentHour.yesterday_cost / hourlyDataToCurrentHour.yesterday_ad_sales
              : 0,
            )}
          </td>
          <td className="text-center">-</td>
        </tr>
        <tr>
          <td>TACOS</td>
          <td className="text-center">
            {percentFormatter(
              liveData.order_item_value > 0 ? hourlyDataToCurrentHour.today_cost / liveData.order_item_value : 0,
            )}
          </td>
          <td className="text-center">
            {percentFormatter(
              hourlyDataToCurrentHour.yesterday_sales > 0 ?
                hourlyDataToCurrentHour.yesterday_cost / hourlyDataToCurrentHour.yesterday_sales
              : 0,
            )}
          </td>
          <td className="text-center">-</td>
        </tr>
      </tbody>
    </Table>
  );
};

const AdTypePieChart = ({ data, isLoading, marketPlace }) => {
  const adTypes = Object.keys(data);
  let total = 0;

  // Filter AdTypes and remove U
  const adTypeData = adTypes
    .filter((adType) => adType !== "U")
    .map((adType) => {
      total += data[adType];
      return {
        name: AD_TYPES[adType],
        y: data[adType],
        color: AD_TYPES_COLORS[adType],
      };
    });

  const options = {
    chart: {
      type: "pie",
      style: {
        fontFamily: "Poppins",
        fontWeight: 300,
        fontSize: "15px",
      },
      height: "400px",
    },
    title: {
      text: null,
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          useHTML: true,
          formatter: function () {
            return (
              `<span style="font-size: 1.2em; font-weight: 600; opacity: 0.8">${this.point.name}</span><br>` +
              `<span style="opacity: 0.6; margin: auto; display: block; text-align: center; width: 100%;">${formatCurrency(
                this.point.y,
                marketPlace.marketPlace,
              )} - ${formatPercent(this.point.y / total, 1)}</span>`
            );
          },
        },
      },
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        const formattedValue = formatCurrency(this.point.y, marketPlace.marketPlace);

        if (total) {
          return (
            "<span><strong>" +
            this.point.name +
            ":</strong><br />" +
            formattedValue +
            "<strong>" +
            ` (${formatPercent(this.point.y / total, 1)})` +
            "</strong></span>"
          );
        }

        return "<span><strong>" + this.point.name + ":</strong><br />" + formattedValue + "</span>";
      },
    },
    series: [
      {
        name: "Ad Type",
        colorByPoint: true,
        data: adTypeData,
      },
    ],
  };

  return (
    <>
      {isLoading && (
        <div className="text-center">
          <i className="fa fa-spinner fa-spin fa-2x"></i>
        </div>
      )}

      {!isLoading && <HighchartsReact highcharts={Highcharts} options={options} />}
    </>
  );
};

const LiveDashboard = ({ user, marketPlace, channel }) => {
  const transition = "";
  const [daysAgo, setDaysAgo] = useState(1);
  const [selectedBucket, setSelectedBucket] = useState("units_ordered");
  const [productSelectedBucket, setProductSelectedBucket] = useState("top_5_products_sold_today");
  const [campaignSelectedBucket, setCampaignSelectedBucket] = useState("highest_spending_campaigns");
  const [selectedHour, setSelectedHour] = useState("chart");

  const daysRotation = {
    1: "Yesterday",
    7: "7 Days Ago",
    14: "14 Days Ago",
    30: "30 Days Ago",
  };

  const buttunBuckets = [
    { label: "Units", value: "units_ordered", counter: undefined },
    { label: "Sales", value: "sales", counter: undefined },
    {
      label: "Imp.",
      value: "impressions",
      counter: undefined,
    },
    { label: "Clicks", value: "clicks", counter: undefined },
    { label: "Spend", value: "cost", counter: undefined },
    { label: "Ad $", value: "ad_sales", counter: undefined },
  ];

  const productButtonBuckets = [
    {
      label: "Top 5 Products Sold Today",
      value: "top_5_products_sold_today",
      counter: undefined,
    },
    {
      label: "Last 5 Products Sold",
      value: "last_5_products_sold",
      counter: undefined,
    },
  ];

  const campaignOptionBuckets = [
    {
      label: "Highest Spending Campaigns",
      value: "highest_spending_campaigns",
      counter: undefined,
    },
    {
      label: "Spend by Campaign Type",
      value: "spend_by_campaign_type",
      counter: undefined,
    },
  ];

  // Current Hour in from UTC to PST (but .tz is not a function in moment)
  const currentHour = moment().utcOffset(-8).format("HH");

  const { dt, yt } = useMemo(() => {
    const dt = moment().format("YYYY-MM-DD");
    const yt = moment().subtract(daysAgo, "days").format("YYYY-MM-DD");

    return { dt, yt };
  }, [daysAgo]);

  const filters = {
    start_date: dt,
    end_date: dt,
    pre_start_date: yt,
    pre_end_date: yt,
    empty: true,
    mp: marketPlace.marketPlace,
    market_place: marketPlace.marketPlace,
    marketplace: marketPlace.marketPlace,
  };

  const currencyFormatter = (value) => {
    return formatCurrency(value, marketPlace.marketPlace);
  };

  const percentFormatter = (value) => {
    return formatPercent(value);
  };
  const {
    isSuccess: adSucceess,
    data: adData,
    isLoading: adIsLoading,
  } = useFetch(["live_ad_report", "live_data", marketPlace.marketPlace], `/merchandise/live_advertisements`, filters, {
    select: (d) => d.data,
    placeholderData: keepPreviousData,
  });

  const { data: adSpendData } = useFetch(
    ["live_ad_spend_report", "live_data", marketPlace.marketPlace],
    `/merchandise/live_ad_spend_sales`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const { data, isLoading } = useFetch(
    ["merchandise_live_orders", "live_data", marketPlace.marketPlace],
    `/merchandise/live_orders`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const { data: hourData } = useFetch(
    ["live_orders_by_hour", "live_data", marketPlace.marketPlace],
    `/merchandise/live_data_by_hour`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const hourChartOptions = useMemo(() => {
    if (!hourData) {
      return null;
    }

    let todayKey = "today_units_ordered";
    let yesterdayKey = "yesterday_units_ordered";

    if (selectedBucket === "sales") {
      todayKey = "today_sales";
      yesterdayKey = "yesterday_sales";
    } else if (selectedBucket === "impressions") {
      todayKey = "today_impressions";
      yesterdayKey = "yesterday_impressions";
    } else if (selectedBucket === "clicks") {
      todayKey = "today_clicks";
      yesterdayKey = "yesterday_clicks";
    } else if (selectedBucket === "cost") {
      todayKey = "today_cost";
      yesterdayKey = "yesterday_cost";
    } else if (selectedBucket === "ad_sales") {
      todayKey = "today_ad_sales";
      yesterdayKey = "yesterday_ad_sales";
    }

    const hours = Object.keys(hourData);
    const todayUnits = hours.map((hour) => (hourData[hour][todayKey] > 0 ? hourData[hour][todayKey] : 0));
    const yesterdayUnits = hours.map((hour) => (hourData[hour][yesterdayKey] > 0 ? hourData[hour][yesterdayKey] : 0));

    const hourChartOptions = {
      chart: {
        type: "column",
        style: {
          fontFamily: "Poppins",
          fontWeight: 300,
          fontSize: "15px",
        },
        height: "300px",
      },
      title: {
        text: null,
      },
      xAxis: {
        categories: hours,
        title: {
          text: "Hour",
        },
      },
      yAxis: {
        title: {
          text: null,
        },
      },
      legend: {
        align: "right",
        verticalAlign: "top",
        layout: "vertical",
        enabled: false,
      },
      plotOptions: {
        column: {
          pointPadding: 0.15,
          borderWidth: 0,
          groupPadding: 0.15,
        },
      },
      series: [
        {
          name: "Today",
          data: todayUnits,
          color: trellisPalette[0],
        },
        {
          name: "Yesterday",
          data: yesterdayUnits,
          color: trellisPalette[2],
        },
      ],
    };

    return hourChartOptions;
  }, [hourData, selectedBucket]);

  const hourlyDataToCurrentHour = useMemo(() => {
    if (!hourData) {
      return {};
    }

    const keys = Object.keys(hourData);
    let stop = false;

    const data = {
      yesterday_sales: 0,
      yesterday_units_ordered: 0,
      yesterday_cost: 0,
      yesterday_ad_sales: 0,
      yesterday_impressions: 0,
      yesterday_view_impressions: 0,
      yesterday_clicks: 0,
      yesterday_attributed_conversions_14d: 0,

      today_sales: 0,
      today_units_ordered: 0,
      today_cost: 0,
      today_ad_sales: 0,
      today_impressions: 0,
      today_view_impressions: 0,
      today_clicks: 0,
      today_attributed_conversions_14d: 0,
    };

    for (let i = 0; i < keys.length; i++) {
      if (currentHour > 0 && i === currentHour - 1) {
        stop = true;
      }

      const dayData = hourData[keys[i]];

      data.today_sales += dayData.today_sales || 0;
      data.today_units_ordered += dayData.today_units_ordered || 0;
      data.today_cost += dayData.today_cost || 0;
      data.today_ad_sales += dayData.today_ad_sales || 0;
      data.today_impressions += dayData.today_impressions || 0;
      data.today_view_impressions += dayData.today_view_impressions || 0;
      data.today_clicks += dayData.today_clicks || 0;
      data.today_attributed_conversions_14d += dayData.today_attributed_conversions_14d || 0;

      data.yesterday_sales += dayData.yesterday_sales || 0;
      data.yesterday_units_ordered += dayData.yesterday_units_ordered || 0;
      data.yesterday_cost += dayData.yesterday_cost || 0;
      data.yesterday_ad_sales += dayData.yesterday_ad_sales || 0;
      data.yesterday_impressions += dayData.yesterday_impressions || 0;
      data.yesterday_view_impressions += dayData.yesterday_view_impressions || 0;
      data.yesterday_clicks += dayData.yesterday_clicks || 0;
      data.yesterday_attributed_conversions_14d += dayData.yesterday_attributed_conversions_14d || 0;

      if (stop) {
        break;
      }
    }
    return data;
  }, [hourData, currentHour]);

  const { data: orgData, isLoading: orgIsLoading } = useFetch(
    ["live_org_report", marketPlace.marketPlace, channel],
    `/api/data_report/category_report`,
    {
      mp: marketPlace.marketPlace,
      channel: channel,
      ...filters,
    },
    {
      select: (d) => d.data,
      default: [],
      placeholderData: keepPreviousData,
    },
  );

  const { acos, tacos } = useMemo(() => {
    if (!orgData || !adSpendData) {
      return {
        acos: 0,
        tacos: 0,
      };
    }

    let acos = 0;
    let tacos = 0;

    if (adSpendData?.attributed_sales_7d > 0) {
      acos = adSpendData?.cost / adSpendData?.attributed_sales_7d;

      if (data?.order_item_value > 0 || orgData?.total?.total_sales > 0) {
        let totalSales = data?.order_item_value;
        if (!totalSales) {
          totalSales = orgData?.total?.total_sales;
        }
        tacos = adSpendData?.cost / totalSales;
      }
    }

    return {
      acos,
      tacos,
    };
  }, [orgData, adSpendData, data]);

  const PerformanceProgressBars = ({ label, current, previous, format, single = false }) => {
    return (
      <div>
        {label && (
          <div className="fs-4 float-start" style={{ width: "100px" }}>
            {label}
          </div>
        )}
        <div className="float-end" style={{ width: label ? "calc(100% - 100px)" : "100%" }}>
          <ProgressBar
            now={current}
            min={0}
            max={previous}
            variant="tr-fuchsia"
            style={{
              height: "20px",
              fontSize: "12px",
            }}
            className="mb-1"
            label={current ? format(current) : "-"}
          ></ProgressBar>
          {!single && (
            <ProgressBar
              now={previous}
              min={0}
              max={previous}
              variant="tr-warning"
              style={{
                height: "20px",
                fontSize: "12px",
              }}
              label={previous ? format(previous) : "-"}
            ></ProgressBar>
          )}
        </div>
      </div>
    );
  };

  const Ad = ({ ad }) => {
    return (
      <div key={ad.ad_id} className={transition}>
        <Row className="pt-3">
          <Col xs={12} xxl={12}>
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
                marginRight: "25px",
              }}
            >
              <Link
                style={{
                  textDecoration: "none",
                  fontWeight: 500,
                  fontSize: "1.5rem",
                  cursor: "pointer",
                }}
                to={`/user/advertising/dashboard/ads/${ad?.ad_id}?tab=details`}
                target="_blank"
              >
                {ad?.ad_name}
              </Link>
            </div>
          </Col>
          <Col xs={12} xxl={12} className="fs-4">
            <Row>
              <Col xs={8} className="text-start">
                <span className="pe-1 fw-bold">
                  <small>
                    Spend / Budget ({currencyFormatter(ad?.daily_budget)}
                    {ad?.daily_budget_override ?
                      ` - increased today to ${currencyFormatter(ad?.daily_budget_override)}`
                    : null}
                    )
                  </small>
                </span>
                <br />
                <PerformanceProgressBars
                  current={ad?.spend}
                  previous={ad?.daily_budget}
                  format={currencyFormatter}
                  single
                />
              </Col>
              <Col xs={2} className="text-center">
                <span className="pe-1 fw-bold">
                  <small>Sales</small>
                </span>
                <br />
                {currencyFormatter(ad?.sales)}
              </Col>
              <Col xs={2} className="text-center">
                <span className="pe-1 fw-bold">
                  <small>ACOS</small>
                </span>
                <br />
                {ad?.sales > 0 ? formatPercent(ad?.spend / ad?.sales) : formatPercent(0)}
              </Col>
              <Col xs={{ offset: 8, span: 4 }}>
                <BudgetIncreaseModal
                  ad={ad}
                  defaultSuggestedBudget={Math.ceil(ad.daily_budget * 1.2)}
                  isUnmanagedAd={ad.ad_goal === 25 && !ad.non_trellis_enrolled_ad_accept_bids}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    );
  };

  const Rotate = () => {
    if (daysAgo === 1) {
      setDaysAgo(7);
    } else if (daysAgo === 7) {
      setDaysAgo(14);
    } else if (daysAgo === 14) {
      setDaysAgo(30);
    } else {
      setDaysAgo(1);
    }
  };

  if (hourlyDataToCurrentHour?.yesterday_impressions === 0) {
    return (
      <Grid
        fluid
        style={{
          minHeight: "calc(100vh - 150px)",
          background: "#fefefe",
        }}
      >
        <Row className="m-2">
          <Col xs={12} className="mx-3 my-5 py-5 text-center">
            <span className="fs-2">Data is currently unavailable for this seller and marketplace.</span>
          </Col>
        </Row>
      </Grid>
    );
  }

  if (user?.organization_type !== "seller") {
    return (
      <Grid
        fluid
        style={{
          minHeight: "calc(100vh - 150px)",
          background: "#fefefe",
        }}
      >
        <Row className="m-2">
          <Col xs={12} className="mx-3 my-5 py-5 text-center">
            <span className="fs-2">Today is not available for Vendor accounts.</span>
          </Col>
        </Row>
      </Grid>
    );
  }

  if (channel !== "amazon") {
    return (
      <Grid
        fluid
        style={{
          minHeight: "calc(100vh - 150px)",
          background: "#fefefe",
        }}
      >
        <Row className="m-2">
          <Col xs={12} className="mx-3 my-5 py-5 text-center">
            <span className="fs-2">Today is only available for Amazon accounts.</span>
          </Col>
        </Row>
      </Grid>
    );
  }

  return (
    <Grid
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <Row className="m-2">
        <Col xs={12} className="mx-3 my-4">
          <span className="fs-1">Today </span>
          <Badge
            className="badge bg-trellis-purple px-3 py-2"
            pill
            style={{
              borderRadius: "5px",
            }}
          >
            <span className="fs-4">Beta</span>
          </Badge>

          <div className="fs-4">
            The Today page will show you realtime metrics and insights on what's happening with your products and
            campaign today.
          </div>
        </Col>
        <Col xs={12} xxl={6} className="pb-3">
          <Card className="mx-2 p-4 mb-3" style={{ height: "100%", borderRadius: "5px" }}>
            <Card.Title>
              <h6 className=" fs-standard text-center" style={{ fontWeight: 600 }}>
                <span
                  className="float-center bg-tr-fuchsia px-2 py-1 me-2"
                  style={{ color: "#FFF", borderRadius: "5px" }}
                >
                  Today
                </span>
                <span className="float-center px-2 py-1 me-2">VS</span>
                <span
                  className="float-center bg-tr-warning px-2 py-1"
                  style={{ color: "#FFF", borderRadius: "5px", cursor: "pointer" }}
                  onClick={() => Rotate()}
                >
                  {daysRotation[daysAgo]}
                </span>
              </h6>
            </Card.Title>
            {(isLoading || orgIsLoading) && (
              <div className="text-center">
                <i className="fa fa-spinner fa-spin fa-2x"></i>
              </div>
            )}
            {!isLoading && !orgIsLoading && (
              <>
                <div className="mt-4">
                  <PerformanceProgressBars
                    label={`Units Sold`}
                    current={data?.quantity_ordered}
                    previous={orgData?.total?.total_units_prev}
                    format={formatNumber}
                  />
                </div>
                <div className="mt-4">
                  <PerformanceProgressBars
                    label={`Total Sales`}
                    current={data?.order_item_value}
                    previous={orgData?.total?.total_sales_prev}
                    format={currencyFormatter}
                  />
                </div>
                <div className="mt-4">
                  <PerformanceProgressBars
                    label={`Ad Spend`}
                    current={adSpendData?.cost}
                    previous={orgData?.total?.cost_prev}
                    format={currencyFormatter}
                  />
                </div>
                <div className="mt-4">
                  <PerformanceProgressBars
                    label={`Ad Sales`}
                    current={adSpendData?.attributed_sales_7d}
                    previous={orgData?.total?.ad_sales_prev}
                    format={currencyFormatter}
                  />
                </div>
                <div className="mt-4">
                  <PerformanceProgressBars
                    label={`ACOS`}
                    current={acos}
                    previous={orgData?.total?.acos_prev}
                    format={percentFormatter}
                  />
                </div>
                <div className="mt-4">
                  <PerformanceProgressBars
                    label={`TACOS`}
                    current={tacos}
                    previous={orgData?.total?.total_acos_prev}
                    format={percentFormatter}
                  />
                </div>
              </>
            )}
          </Card>
        </Col>
        <Col xs={12} xxl={6} className="pb-3">
          <Card className="mx-2 p-4 mb-3" style={{ height: "100%", borderRadius: "5px" }}>
            <Card.Title>
              <h6 className=" fs-standard text-center" style={{ fontWeight: 600 }}>
                {selectedHour === "chart" && <>Performance by Hour (PST)</>}
                {selectedHour === "table" && (
                  <>
                    Performance from Midnight to&nbsp;
                    {moment().utcOffset(-9).format("HH:mm")} PST
                  </>
                )}
                <span className="float-end mx-2" style={{ cursor: "pointer" }} onClick={() => setSelectedHour("table")}>
                  <FaTable />
                </span>
                <span className="float-end mx-2" style={{ cursor: "pointer" }} onClick={() => setSelectedHour("chart")}>
                  <IoStatsChart />
                </span>
              </h6>
            </Card.Title>

            {selectedHour === "chart" && (
              <div>
                <Row>
                  <Col
                    xs={12}
                    className="m-0 p-0 mb-3 ps-xxl-5 ms-xxl-5"
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                    <ButtonGroup
                      buttons={buttunBuckets}
                      value={selectedBucket}
                      onSelect={(values) => {
                        setSelectedBucket(values);
                        // setInsightPlans(null);
                      }}
                    />
                  </Col>
                </Row>
                {hourChartOptions && <HighchartsReact highcharts={Highcharts} options={hourChartOptions} />}
              </div>
            )}

            {selectedHour === "table" && (
              <div className="mb-4">
                <PerformanceTable
                  currencyFormatter={currencyFormatter}
                  percentFormatter={percentFormatter}
                  hourlyDataToCurrentHour={hourlyDataToCurrentHour}
                  liveData={data}
                  liveadSpendData={adSpendData}
                />
              </div>
            )}

            {!hourChartOptions && (
              <div className="text-center">
                <i className="fa fa-spinner fa-spin fa-2x"></i>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={12} xxl={6} className="pb-3">
          <Card className="mx-2 p-4 mb-3" style={{ height: "100%", borderRadius: "5px" }}>
            <Card.Title>
              <Row>
                <Col xs={12} className="m-0 p-0 mt-3 ps-xxl-5" style={{ display: "flex", justifyContent: "center" }}>
                  <ButtonGroup
                    buttons={campaignOptionBuckets}
                    value={campaignSelectedBucket}
                    onSelect={(values) => {
                      setCampaignSelectedBucket(values);
                    }}
                  />
                </Col>
              </Row>
            </Card.Title>
            <Row>
              <Col xs={12}>
                {adIsLoading && (
                  <div className="text-center">
                    <i className="fa fa-spinner fa-spin fa-2x"></i>
                  </div>
                )}
                {adSucceess &&
                  !adIsLoading &&
                  campaignSelectedBucket === "highest_spending_campaigns" &&
                  adData?.ad_results.map((ad) => <Ad key={ad.ad_id} ad={ad} />)}

                {adSucceess && !adIsLoading && campaignSelectedBucket === "spend_by_campaign_type" && (
                  <AdTypePieChart data={adData?.spend_by_type} isLoading={adIsLoading} marketPlace={marketPlace} />
                )}
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={12} xxl={6} className="pb-3">
          <Card className="mx-2 p-4 mb-3" style={{ height: "100%", borderRadius: "5px" }}>
            <Card.Title>
              <Row>
                <Col
                  xs={12}
                  className="m-0 p-0 mt-3 ps-xxl-5 ms-xxl-5"
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <ButtonGroup
                    buttons={productButtonBuckets}
                    value={productSelectedBucket}
                    onSelect={(values) => {
                      setProductSelectedBucket(values);
                      // setInsightPlans(null);
                    }}
                  />
                </Col>
              </Row>
            </Card.Title>
            <Row>
              <Col xs={12}>
                {productSelectedBucket === "last_5_products_sold" && (
                  <LastFiveProductSold data={data} isLoading={isLoading} transition={transition} />
                )}

                {productSelectedBucket === "top_5_products_sold_today" && (
                  <TopFiveProductsSoldToday data={data} isLoading={isLoading} transition={transition} />
                )}
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={12} xxl={6} className="pb-3"></Col>
      </Row>
    </Grid>
  );
};

const mapStateToProps = (state) => ({
  marketPlace: state.marketPlace,
  user: state.user,
  channel: state.channel.currentChannel,
});

export default connect(mapStateToProps)(LiveDashboard);
